import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { Optional, ResultKind } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';
import { Pangolin } from 'services/accounts/types';

import {
  IPangolinDomain,
  UpdateRestoreStatus,
  UpdateRestore,
  UpdateSnapshot,
  UpdateHistoryType,
  UpdateHistoryRef,
} from './types';

import {
  UpdateRestoreStatusMap as UpdateRestoreStatusMapProto,
  UpdateRestoreStatus as UpdateRestoreStatusProto,
  UpdateRestoreObject as UpdateRestoreProto,

  CreateUpdateRestoreRequest,
  CreateUpdateRestoreResponse,
  GetUpdateRestoreResponse,
} from './protos/update_pb';

export type CreateUpdateRestoreResult =
  | { kind: ResultKind.OK; restore: UpdateRestore }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export type CancelUpdateRestoreResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class UpdateRestoreService {
  static async fetchActive(pangolin: Pangolin, domain: IPangolinDomain, save = true): Promise<Optional<UpdateRestore>> {
    try {
      const httpResp = await http.get(`/pangolin/${domain.id}/update/restore`);
      return runInAction(() => {
        const remoteRestore = parseGetUpdateRestoreResponse(httpResp);

        if (save) {
          if (remoteRestore) {
            const localRestore = domain.updateRestores.get(remoteRestore.id);
            if (localRestore) { Object.assign(localRestore, remoteRestore); }
            else { domain.updateRestores.set(remoteRestore.id, remoteRestore); }
            pangolin.updateActiveRestoreID = remoteRestore.id;
          } else {
            pangolin.updateActiveRestoreID = undefined;
          }
        }

        return remoteRestore;
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      if (status === 404) return undefined;
      throw error;
    }
  }

  static async dispatch(domain: IPangolinDomain, pangolin: Pangolin, snapshot: UpdateSnapshot, password: string): Promise<CreateUpdateRestoreResult> {
    const request = createCreateUpdateRestoreRequest(snapshot, password);
    try {
      const httpResp = await http.post(`/pangolin/${domain.id}/update/restore`, createRequestString(request));
      const remoteRestore = parseCreateUpdateRestoreResponse(httpResp);
      return runInAction(() => {
        if (remoteRestore != null) {
          const remoteHistory = historyFromRestore(remoteRestore);
          domain.updateRestores.set(remoteRestore.id, remoteRestore);
          domain.updateHistory.setAt(remoteRestore.id, remoteHistory, 0);
          pangolin.updateActiveRestoreID = remoteRestore.id;
          return { kind: ResultKind.OK, restore: remoteRestore };
        }

        return { kind: ResultKind.SYSTEM_ERROR, status: 500 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async cancel(domain: IPangolinDomain, pangolin: Pangolin): Promise<CancelUpdateRestoreResult> {
    try {
      await http.delete(`/pangolin/${domain.id}/update/restore`);
      return runInAction(() => {
        if (pangolin.updateActiveRestoreID != null) {
          const localRestore = domain.updateRestores.get(pangolin.updateActiveRestoreID);
          if (localRestore != null) {
            localRestore.terminal_status = localRestore.status;
            localRestore.status = UpdateRestoreStatus.CANCELLED;
          }

          pangolin.updateActiveRestoreID = undefined;
        }

        return { kind: ResultKind.OK };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function historyFromRestore(restore: UpdateRestore): UpdateHistoryRef {
  return { type: UpdateHistoryType.RESTORE, restore_id: restore.id };
}

function parseGetUpdateRestoreResponse(httpResp: AxiosResponse): Optional<UpdateRestore> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateRestoreResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getRestore();
  return restoreFromProto(itemProto);
}

function parseCreateUpdateRestoreResponse(httpResp: AxiosResponse): Optional<UpdateRestore> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = CreateUpdateRestoreResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getRestore();
  return restoreFromProto(itemProto);
}

type UpdateRestoreStatusValueProto = UpdateRestoreStatusMapProto[keyof UpdateRestoreStatusMapProto];
function updateRestoreStatusFromProto(proto: Optional<UpdateRestoreStatusValueProto>): Optional<UpdateRestoreStatus> {
  switch (proto) {
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_PENDING:
      return UpdateRestoreStatus.PENDING;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_ROLLBACK:
      return UpdateRestoreStatus.ROLLBACK;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_DISPATCH:
      return UpdateRestoreStatus.DISPATCH;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_DOWNLOAD:
      return UpdateRestoreStatus.DOWNLOAD;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_INSTALL:
      return UpdateRestoreStatus.INSTALL;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_REBOOT:
      return UpdateRestoreStatus.REBOOT;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_COMPLETED:
      return UpdateRestoreStatus.COMPLETED;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_CANCELLED:
      return UpdateRestoreStatus.CANCELLED;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_SKIPPED:
      return UpdateRestoreStatus.SKIPPED;
    case UpdateRestoreStatusProto.UPDATE_RESTORE_STATUS_FAILED:
      return UpdateRestoreStatus.FAILED;
    default:
      return undefined;
  }
}

export function restoreFromProto(proto: Optional<UpdateRestoreProto>): Optional<UpdateRestore> {
  if (proto == null) return undefined;

  const id = proto.getId();
  const status = updateRestoreStatusFromProto(proto.getStatus());
  const source_release_id = proto.getSourceReleaseId();
  const target_release_id = proto.getTargetReleaseId();
  const target_snapshot_id = proto.getTargetSnapshotId();
  const created_at = proto.getCreatedAt();
  const updated_at = proto.getUpdatedAt();
  const terminal_status = (
    !proto.hasTerminalStatus() ? undefined :
    updateRestoreStatusFromProto(proto.getTerminalStatus())
  );

  if (id === undefined) return undefined;
  if (status === undefined) return undefined;
  if (target_release_id === undefined) return undefined;
  if (target_snapshot_id === undefined) return undefined;
  if (created_at === undefined) return undefined;
  if (updated_at === undefined) return undefined;

  return {
    id, status,
    source_release_id,
    target_release_id,
    target_snapshot_id,
    created_at: created_at.toDate(),
    updated_at: updated_at.toDate(),
    terminal_status,
  };
}

function createCreateUpdateRestoreRequest(snapshot: UpdateSnapshot, password: string): CreateUpdateRestoreRequest {
  const proto = new CreateUpdateRestoreRequest();
  proto.setSnapshotId(snapshot.id);
  proto.setPassword(password);
  return proto;
}